import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import config from '../config';

// Must match backend's config.RESERVED_SUBDOMAINS and AppWrapper.js
const RESERVED_SUBDOMAINS = new Set([
  'admin', 'api', 'widget', 'app', 'www', 'staging',
]);

const TenantContext = createContext();

export const useTenant = () => {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
};

/**
 * TenantProvider — resolves the current tenant from the backend on mount.
 *
 * Calls GET /api/resolve-tenant which uses the Host header to determine
 * the tenant. Stores the resolved tenant info (slug, name, enabled_modules,
 * available_modules) and provides helper functions.
 *
 * Also detects whether we're on the bare platform domain (no subdomain)
 * which means we should show the platform admin UI instead of tenant UI.
 */
export const TenantProvider = ({ children }) => {
  const [tenant, setTenant] = useState(null);
  const [availableModules, setAvailableModules] = useState({});
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState(null);
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false);

  // Platform-wide maintenance mode — checked on mount so the tenant
  // frontend can show a blocking overlay before rendering anything.
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [maintenanceMessage, setMaintenanceMessage] = useState('');

  /**
   * Redirect to the base URL (strip the invalid subdomain).
   * In local dev → http://localhost:PORT, in production → https://BASE_DOMAIN
   */
  const redirectToBase = useCallback(() => {
    const { protocol, port } = window.location;
    const baseDomain = process.env.REACT_APP_BASE_DOMAIN;
    const portSuffix = port && port !== '443' && port !== '80' ? `:${port}` : '';

    if (baseDomain) {
      window.location.href = `${protocol}//${baseDomain}${portSuffix}`;
    } else {
      // Local dev fallback — go to plain localhost
      window.location.href = `${protocol}//localhost${portSuffix}`;
    }
  }, []);

  /**
   * Check if the browser URL has a subdomain that doesn't match the
   * resolved tenant slug. Returns true if there's a mismatch (meaning
   * the user is on a non-existent tenant subdomain).
   */
  const isSubdomainMismatch = useCallback((resolvedSlug) => {
    const hostname = window.location.hostname;
    const baseDomain = process.env.REACT_APP_BASE_DOMAIN;

    // Local dev: e.g. "bogus.localhost" → subdomain "bogus" vs resolved "default"
    if (hostname.endsWith('.localhost') || hostname.endsWith('.127.0.0.1')) {
      const subdomain = hostname.split('.')[0];
      return subdomain && resolvedSlug !== subdomain;
    }

    // Production: e.g. "bogus.reruptionchat.com" vs resolved slug
    if (baseDomain && hostname.endsWith('.' + baseDomain)) {
      const subdomain = hostname.slice(0, -(baseDomain.length + 1));
      return subdomain && resolvedSlug !== subdomain;
    }

    return false;
  }, []);

  const fetchTenant = useCallback(async () => {
    try {
      const response = await fetch(`${config.api.baseUrl}/api/resolve-tenant`, {
        credentials: 'include',
      });

      if (!response.ok) {
        // 404 means no tenant resolved — if on a subdomain, redirect to base;
        // if on the bare domain, show platform admin UI
        if (response.status === 404) {
          // If impersonating and the tenant doesn't exist, bail out
          const impersonating = sessionStorage.getItem('impersonate_tenant');
          if (impersonating) {
            sessionStorage.removeItem('impersonate_tenant');
            window.location.reload();
            return;
          }

          const hostname = window.location.hostname;
          const baseDomain = process.env.REACT_APP_BASE_DOMAIN;

          // Platform admin context: bare domain, localhost, or a reserved
          // infra subdomain like "admin.staging.reruptionchat.com"
          const isBareDomain = (baseDomain && hostname === baseDomain)
            || hostname === 'localhost';
          const sub = baseDomain && hostname.endsWith('.' + baseDomain)
            ? hostname.slice(0, -(baseDomain.length + 1)) : null;
          const isReservedSubdomain = sub && !sub.includes('.')
            && RESERVED_SUBDOMAINS.has(sub);

          // Check if we're on a custom domain (not our base domain or subdomain)
          const isCustomDomain = baseDomain
            && hostname !== baseDomain
            && !hostname.endsWith('.' + baseDomain)
            && hostname !== 'localhost';

          if (isBareDomain || isReservedSubdomain) {
            setIsPlatformAdmin(true);
          } else if (isCustomDomain) {
            // Custom domain that didn't resolve — show error, don't redirect
            // away from the tenant's own domain
            setError('Tenant not found for this domain');
          } else {
            redirectToBase();
            return;
          }
          setIsLoaded(true);
          return;
        }
        throw new Error('Failed to resolve tenant');
      }

      const data = await response.json();
      setAvailableModules(data.available_modules || {});

      // On custom domains, cache the resolved slug so the fetch interceptor
      // in AppWrapper.js can include X-Tenant-Slug on all subsequent API calls
      // (instead of X-Custom-Domain which is only needed for the first request).
      if (data.slug && window._setCustomDomainSlug) {
        window._setCustomDomainSlug(data.slug);
      }

      // When a super-admin is impersonating a tenant via sessionStorage,
      // skip all hostname-based platform-admin and subdomain-mismatch checks
      // — we trust the X-Tenant-Slug header to resolve the right tenant.
      const impersonating = sessionStorage.getItem('impersonate_tenant');

      if (!impersonating) {
        const hostname = window.location.hostname;
        const baseDomain = process.env.REACT_APP_BASE_DOMAIN;

        // Bare platform domain or reserved infra subdomain (admin, widget, etc.)
        const sub = baseDomain && hostname.endsWith('.' + baseDomain)
          ? hostname.slice(0, -(baseDomain.length + 1)) : null;
        if ((baseDomain && hostname === baseDomain)
            || (sub && !sub.includes('.') && RESERVED_SUBDOMAINS.has(sub))) {
          setIsPlatformAdmin(true);
          setIsLoaded(true);
          return;
        }

        // Subdomain doesn't match the resolved tenant → non-existent tenant,
        // redirect to the base URL instead of showing a broken chat UI
        if (isSubdomainMismatch(data.slug)) {
          redirectToBase();
          return;
        }
      }

      // Valid tenant — use it
      setTenant(data);

      // Check platform-wide maintenance status so we can block the UI
      // before any tenant content renders. Fire-and-forget on error —
      // if the check fails, we default to "not in maintenance" (fail-open).
      try {
        const mResp = await fetch(`${config.api.baseUrl}/api/platform/maintenance`, {
          credentials: 'include',
        });
        if (mResp.ok) {
          const mData = await mResp.json();
          setMaintenanceMode(mData.enabled === true);
          setMaintenanceMessage(mData.message || '');
        }
      } catch (_) { /* fail open — assume no maintenance */ }

      setIsLoaded(true);
    } catch (e) {
      console.error('Failed to resolve tenant:', e);
      setError(e.message);

      // If we're on a subdomain and the fetch failed (CORS / network error),
      // redirect to the base URL rather than showing a broken page
      const hostname = window.location.hostname;
      if (hostname.endsWith('.localhost') || hostname.endsWith('.127.0.0.1')) {
        redirectToBase();
        return;
      }

      setIsLoaded(true);
    }
  }, [redirectToBase, isSubdomainMismatch]);

  useEffect(() => {
    fetchTenant();
  }, [fetchTenant]);

  /**
   * Check if the current tenant has a specific module enabled.
   * Returns true for any module marked always_enabled in the registry,
   * even if it's missing from the tenant's enabled_modules array in the DB.
   */
  const hasModule = useCallback((moduleKey) => {
    // Check the registry for always_enabled modules (covers base_chat, custom_branding, etc.)
    if (availableModules[moduleKey]?.always_enabled) return true;
    if (!tenant || !tenant.enabled_modules) return false;
    return tenant.enabled_modules.includes(moduleKey);
  }, [tenant, availableModules]);

  const value = {
    tenant,
    isLoaded,
    error,
    isPlatformAdmin,
    availableModules,
    hasModule,
    refreshTenant: fetchTenant,

    // Platform-wide maintenance mode
    maintenanceMode,
    maintenanceMessage,

    // Convenience getters
    tenantSlug: tenant?.slug || null,
    tenantName: tenant?.name || null,
    enabledModules: tenant?.enabled_modules || [],
    // Whether this tenant is a demo account (used for auto-redirect to /demo-preview)
    isDemo: tenant?.is_demo || false,
  };

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
};
